import { ReactNode, useId } from "react";

// The Steerly steering-wheel mark (mirrors public/favicon.svg geometry: a
// hub-and-spokes wheel with a heading pointer). `currentColor` drives the
// wheel fill so the surrounding CSS owns the accent + animation. The outer
// ring is a separate stroked circle so it can rotate independently of the
// wheel's one-time settle.
export function SteerlyMark({ className }: { className?: string }) {
  // A stable, instance-unique id so multiple SteerlyMarks on the same page
  // (the brand panel + the stage header, plus any stacked AuthShells) don't
  // collide on a shared mask id and cross-reference each other's mask.
  const maskId = useId();
  return (
    <svg
      className={className}
      viewBox="0 0 48 48"
      role="img"
      aria-label="Steerly"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <circle className="steerly-mark__ring" cx="24" cy="24" r="21" stroke="currentColor" />
      <g className="steerly-mark__wheel">
        <mask id={maskId}>
          <circle cx="24" cy="24" r="13" fill="#fff" />
          <polygon points="24,18.9 29.4,28.9 18.6,28.9" fill="#000" transform="rotate(12 24 24)" />
          <rect
            x="22.8"
            y="24"
            width="2.4"
            height="14.6"
            fill="#000"
            transform="rotate(12 24 24)"
          />
          <rect
            x="22.8"
            y="24"
            width="2.4"
            height="14.6"
            fill="#000"
            transform="rotate(132 24 24)"
          />
          <rect
            x="22.8"
            y="24"
            width="2.4"
            height="14.6"
            fill="#000"
            transform="rotate(252 24 24)"
          />
        </mask>
        <circle cx="24" cy="24" r="13" fill="currentColor" mask={`url(#${maskId})`} />
      </g>
    </svg>
  );
}

// The three firewall verdicts, reused as the brand panel's proof points — the
// product's actual decision surface (allow / ask / deny) doubling as the
// login's reason-to-believe.
const SIGNALS: Array<{ kind: string; label: string; copy: string }> = [
  { kind: "allow", label: "Allow", copy: "Safe agent actions run untouched." },
  { kind: "ask", label: "Ask", copy: "Risky calls pause for one-tap approval." },
  { kind: "deny", label: "Deny", copy: "Secret reads & force-pushes blocked in real time." },
];

// Full-screen split shell shared by every pre-app surface: sign-in, the
// "connecting" status states, and the checkout launch. The brand viewport is
// fixed copy; `children` is whatever interactive card the stage needs to host.
// `wide` drops the brand aside and opens the stage to the full viewport
// (single centered column, stage brand shown) — used by the plan gate, whose
// three tier cards don't fit the split layout's half-width stage.
export function AuthShell({ children, wide = false }: { children: ReactNode; wide?: boolean }) {
  if (wide) {
    return (
      <div className="auth-screen auth-screen--wide">
        <main className="auth-stage">
          <div className="auth-stage__inner">
            <div className="auth-stage__brand">
              <SteerlyMark className="steerly-mark" />
              <span>Steerly</span>
            </div>
            {children}
          </div>
        </main>
      </div>
    );
  }
  return (
    <div className="auth-screen">
      <aside className="auth-brand" aria-hidden="true">
        <div className="auth-brand__grid" />
        <div className="auth-brand__glow" />
        <div className="auth-brand__inner">
          <div className="auth-brand__logo">
            <SteerlyMark className="steerly-mark" />
            <span>Steerly</span>
          </div>
          <div className="auth-brand__copy">
            <h1 className="auth-brand__headline">
              Put your hands
              <br />
              on the wheel.
            </h1>
            <p className="auth-brand__sub">
              The control plane for your AI agents — govern, observe and steer every coding agent on
              your team.
            </p>
            <ul className="auth-signals">
              {SIGNALS.map((s) => (
                <li key={s.kind} className="auth-signal">
                  <span className={`auth-signal__dot auth-signal__dot--${s.kind}`} />
                  <span className="auth-signal__label">{s.label}</span>
                  <span className="auth-signal__copy">{s.copy}</span>
                </li>
              ))}
            </ul>
          </div>
          <p className="auth-brand__foot">SOC 2-aligned · your code never leaves your machine</p>
        </div>
      </aside>

      <main className="auth-stage">
        <div className="auth-stage__inner">
          <div className="auth-stage__brand">
            <SteerlyMark className="steerly-mark" />
            <span>Steerly</span>
          </div>
          {children}
        </div>
      </main>
    </div>
  );
}
